import React, { useEffect } from 'react';
import { Form, Button, Card, Space, Divider, Spin } from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { usePartner, useCreatePartner, useUpdatePartner } from '../hooks';
import { PartnerBasicInfo, PartnerMAIBInfo } from '../components/partners';
import { preparePartnerFormData } from '../utils/helpers';
import { PARTNER_STATUS } from '../utils/constants';
import { partnersApi } from '../api';

const PartnerForm = () => {
  const history = useHistory();
  const { partner_id } = useParams();
  const [form] = Form.useForm();

  const isEditMode = !!partner_id && partner_id !== 'new';

  const { data: partnerData, isLoading } = usePartner(partner_id, {
    enabled: isEditMode,
  });

  const createPartner = useCreatePartner();
  const updatePartner = useUpdatePartner();

  useEffect(() => {
    if (isEditMode && partnerData) {
      const partner = partnerData.data || partnerData;
      form.setFieldsValue({
        name: partner.name,
        email: partner.email,
        phone: partner.phone,
        description: partner.description,
        status: partner.status,
        logo: partner.logo || null,
        seats: partner.seats || null,
        maib_project_id: partner.maib_project_id,
        maib_project_secret_key: partner.maib_project_secret_key,
        maib_signature_key: partner.maib_signature_key,
      });
    } else {
      // Set defaults for new partner
      form.setFieldsValue({
        status: PARTNER_STATUS.ACTIVE,
      });
    }
  }, [isEditMode, partnerData, form]);

  const handleSubmit = async (values) => {
    const formData = preparePartnerFormData(values);
    const logoFile = values.logo instanceof File ? values.logo : null;

    try {
      let id = partner_id;
      if (isEditMode) {
        await updatePartner.mutateAsync({ id: partner_id, data: formData });
      } else {
        const result = await createPartner.mutateAsync(formData);
        id = result?.data?.id || result?.id;
      }

      // Upload logo if a new file was selected
      if (logoFile && id) {
        await partnersApi.uploadLogo(id, logoFile);
      }

      history.push('/b2b/partners');
    } catch (error) {
      console.error('Error saving partner:', error);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  if (isEditMode && isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" tip="Loading partner..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleCancel}
              type="text"
              aria-label="Go back"
            />
            {isEditMode ? 'Edit Partner' : 'New Partner'}
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <PartnerBasicInfo form={form} />

          <Divider orientation="left">MAIB Payment Integration (Optional)</Divider>

          <PartnerMAIBInfo />

          <Divider />

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={createPartner.isLoading || updatePartner.isLoading}
            >
              {isEditMode ? 'Update Partner' : 'Create Partner'}
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default PartnerForm;